import { useEffect, useMemo, useState } from "react";
import { Search, X, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { getProverbs } from "@/lib/api";
import { Ornament } from "@/components/Ornament";

export default function Proverbs() {
  const [proverbs, setProverbs] = useState([]);
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("Tümü");

  useEffect(() => {
    getProverbs().then(setProverbs).catch(() => {});
  }, []);

  const themes = useMemo(
    () => ["Tümü", ...Array.from(new Set(proverbs.map((p) => p.theme)))],
    [proverbs]
  );

  const filtered = proverbs.filter((p) => {
    const matchTheme = theme === "Tümü" || p.theme === theme;
    const text = `${p.original} ${p.modern} ${p.explanation}`.toLowerCase();
    const matchQ = !q || text.includes(q.toLowerCase());
    return matchTheme && matchQ;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow">Savlar</span>
        <h1 className="mt-3 font-[Cinzel] text-4xl sm:text-5xl font-bold text-foreground">
          Bozkırın Bilgeliği
        </h1>
        <p className="mt-4 text-foreground/75">
          Kaşgarlı'nın "sav" dediği atasözleri, dokuz asır öncesinden bugüne seslenen
          özlü sözler. Her birinin günümüz karşılığı ve açıklamasıyla.
        </p>
        <div className="flex justify-center text-accent mt-6">
          <Ornament className="w-40 h-5" />
        </div>
      </div>

      <div className="mt-8 space-y-5">
        <div className="relative max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            data-testid="proverb-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sav içinde ara…"
            className="w-full rounded-full border border-border bg-card pl-12 pr-11 py-3.5 outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 transition-colors"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Temizle"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-2" data-testid="proverb-theme-filter">
          {themes.map((t) => (
            <button
              key={t}
              onClick={() => setTheme(t)}
              data-testid={`theme-${t}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                theme === t
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border bg-card text-foreground/70 hover:border-accent"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="proverbs-grid">
        {filtered.map((p, i) => (
          <motion.article
            key={p.id}
            data-testid={`proverb-card-${p.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
            className="card-hover rounded-lg border border-border bg-card p-6 flex flex-col"
          >
            <div className="flex items-center justify-between">
              <span className="eyebrow">Sav</span>
              <span className="text-[0.65rem] font-mono uppercase tracking-wider text-muted-foreground border border-border rounded px-2 py-0.5">
                {p.theme}
              </span>
            </div>
            <Quote className="w-5 h-5 text-accent mt-4" />
            <p className="mt-2 font-[Cinzel] text-xl leading-snug text-foreground">{p.original}</p>
            <div className="gold-rule my-4" />
            <p className="rubric italic">{p.modern}</p>
            {p.explanation && <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{p.explanation}</p>}
          </motion.article>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-muted-foreground mt-12">Aramanıza uygun sav bulunamadı.</p>
      )}
    </div>
  );
}
